import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, Copy, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const ref = useRef();

  const load = () => api.get("/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/media/upload", fd);
      }
      toast.success("Uploaded");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (m) => {
    try {
      await api.delete(`/media/${m.id}`);
      toast.success("Deleted");
      load(); setActive(null);
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(err.response.data.detail + "\n\nDelete anyway?")) {
          await api.delete(`/media/${m.id}?force=true`);
          toast.success("Deleted"); load(); setActive(null);
        }
      } else toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveMeta = async (m) => {
    await api.patch(`/media/${m.id}`, { alt: m.alt, caption: m.caption });
    toast.success("Saved"); load();
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("URL copied"); };
  const filtered = items.filter((m) => (m.filename || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-serif-display text-2xl font-bold text-[#111827]">Media Library</h1>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="border border-slate-300 pl-9 pr-3 py-2 text-sm" />
          </div>
          <input type="file" accept="image/*" multiple ref={ref} className="hidden" onChange={upload} />
          <button data-testid="media-upload" onClick={() => ref.current.click()} disabled={busy}
            className="inline-flex items-center gap-2 bg-[#0F2C59] text-white px-4 py-2 text-sm font-semibold hover:bg-[#16447F] disabled:opacity-50">
            <UploadCloud size={16} /> {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-slate-300 text-slate-400">No images yet. Click Upload to add.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <div key={m.id} data-testid={`media-${m.id}`} className="border border-slate-200 bg-white group">
              <button onClick={() => setActive(m)} className="block w-full aspect-square bg-slate-50 overflow-hidden">
                <img src={m.url} alt={m.alt || m.filename} className="w-full h-full object-contain" />
              </button>
              <div className="p-2">
                <p className="text-xs truncate text-slate-600">{m.filename}</p>
                <p className="text-[10px] text-slate-400">{m.width && m.height ? `${m.width}×${m.height} · ` : ""}{Math.round((m.size || 0) / 1024)} KB</p>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => copy(m.url)} className="p-1 text-slate-400 hover:text-[#0F2C59]" title="Copy URL"><Copy size={14} /></button>
                  <button onClick={() => del(m)} data-testid={`media-delete-${m.id}`} className="p-1 text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <img src={active.url} alt="" className="w-full max-h-64 object-contain bg-slate-50 mb-4" />
            <p className="text-xs text-slate-500 mb-3 break-all">URL: {active.url}</p>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Alt text (for accessibility & SEO)</label>
            <input className="w-full border border-slate-300 px-3 py-2 text-sm mb-3" value={active.alt || ""} onChange={(e) => setActive({ ...active, alt: e.target.value })} />
            <label className="block text-xs font-semibold text-slate-600 mb-1">Caption</label>
            <input className="w-full border border-slate-300 px-3 py-2 text-sm mb-4" value={active.caption || ""} onChange={(e) => setActive({ ...active, caption: e.target.value })} />
            <div className="flex gap-2">
              <button onClick={() => saveMeta(active)} className="bg-[#0F2C59] text-white px-4 py-2 text-sm font-semibold">Save</button>
              <button onClick={() => copy(active.url)} className="border border-slate-300 px-4 py-2 text-sm">Copy URL</button>
              <button onClick={() => del(active)} className="text-red-500 px-4 py-2 text-sm ml-auto">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
